// Vendor portal (ERP-PLAN §5.8, migrations 048, 049) on the vendor panel: a contact with Vendor portal ticked signs in
// (their sign-in link sent or copied from the contact), and the products they added on their side, each to link to a
// part (one we have, or a new one made from it) or leave alone. What they change on their parts shows in Supplies.
// Open their portal (migration 056): any staff member into the vendor's portal, acting for them (server/src/portal/staff.ts).
import { Copy, ExternalLink, Link2, Plus, Send } from "lucide-react";
import { useState } from "react";
import type { Item, Vendor, VendorContact, VendorItem, VendorProduct } from "../../../shared/db.gen.ts";
import { skuPrefix } from "../../../shared/part-sku.ts";
import { categoryOptions, categoryPatch, type ItemType, typeOptions, typePatch } from "../../../shared/part-type.ts";
import { linkedName } from "../../../shared/portal.ts";
import { postJson } from "../lib/bills.ts";
import { useBomIndex } from "../lib/boms.ts";
import { ago, dayText, money, qty } from "../lib/format.ts";
import { can, mutate, mutateWithResult } from "../lib/mutate.ts";
import { me } from "../lib/session.ts";
import { useRows } from "../lib/sync.ts";
import { CreateDialog } from "../ui/CreateDialog.tsx";
import { Badge, Button, Select, Thumb } from "../ui/controls.tsx";
import { CellControls, type Column, DataTable } from "../ui/DataTable.tsx";
import { Popover } from "../ui/Popover.tsx";
import { Section } from "../ui/RecordPanel.tsx";
import { toast } from "../ui/toast.tsx";
import { PartPicker } from "./BomPicker.tsx";

const LANGS = [
  { value: "en", label: "English" },
  { value: "zh", label: "中文" },
];

/**
 * A contact with Vendor portal ticked: when they last signed in, their language, and their sign-in link (emailed from
 * the mailbox their POs go from, or copied to send another way). Says when they can't email themselves a new one.
 */
export function ContactPortal({ contact: c, vendor, editable }: { contact: VendorContact; vendor: Vendor; editable: boolean }) {
  const settings = useRows("po_email_settings")[0];
  const mailboxes = useRows("gmail_account");
  const [sending, setSending] = useState(false);
  const sender = (vendor.po_sender_email || settings?.default_sender_email || "").toLowerCase();
  const selfServe = mailboxes.some((m) => m.email.toLowerCase() === sender && m.shared && m.status === "connected");
  const send = async () => {
    setSending(true);
    try {
      const r = await postJson<{ to: string; from: string; expires_at: string }>(`/api/vendor-contacts/${c.id}/sign-in-email`);
      toast.ok(`Sign-in link sent to ${r.to} from ${r.from}. It signs in once, until ${dayText(r.expires_at)}.`);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setSending(false);
    }
  };
  const copy = async () => {
    const { ok, result } = await mutateWithResult("vendor_contact.sign_in_link", { id: c.id });
    if (!ok) return;
    const { token, expires_at } = result as { token: string; expires_at: string };
    const url = `${location.origin}/v/sign-in#${token}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.ok(`Sign-in link copied: send it to ${c.email}. It signs in once, until ${dayText(expires_at)}.`);
    } catch {
      prompt("Copy this sign-in link and send it to them:", url);
    }
  };
  return (
    <div className="mx-3 my-1 flex flex-wrap items-center gap-2 rounded-md bg-surface-2 px-2 py-1.5 text-[12px]">
      <span className="min-w-0 flex-1 basis-40 text-muted">
        Signs in at /v · {c.portal_last_login_at ? `last signed in ${ago(c.portal_last_login_at)}` : "hasn't signed in yet"}
        {!selfServe && (
          <span className="block text-faint" title="Settings → Email: tick Shared on the mailbox their POs go from (or the default sender)">
            Can't email themselves a new link until the mailbox their POs go from is shared (Settings → Email).
          </span>
        )}
      </span>
      <div className="w-24 shrink-0">
        <Select
          aria-label="Portal language"
          value={c.portal_lang}
          disabled={!editable}
          onChange={(e) => mutate("vendor_contact.update", { id: c.id, patch: { portal_lang: e.target.value as VendorContact["portal_lang"] } })}
          options={LANGS}
        />
      </div>
      {editable && (
        <>
          <Button
            size="sm"
            variant="primary"
            icon={<Send className="size-3.5" />}
            disabled={sending}
            onClick={send}
            title="Emails them a link that signs them in once within a week"
          >
            Send sign-in link
          </Button>
          <Button size="sm" icon={<Copy className="size-3.5" />} onClick={copy} title="A link to send another way (WeChat)">
            Copy link
          </Button>
        </>
      )}
    </div>
  );
}

const linkTo = async (p: VendorProduct, item: Pick<Item, "id" | "sku">) => {
  if (await mutate("vendor_product.link", { id: p.id, item_id: item.id })) toast.ok(`Linked to ${item.sku}: their SKU and name are on its vendor link now`);
};

/** Link to a part: the part it is, picked (the BOM index is only read once this opens). */
function LinkPicker({ product, close }: { product: VendorProduct; close: () => void }) {
  const ix = useBomIndex();
  return (
    <PartPicker
      ix={ix}
      autoFocus
      inline
      placeholder="The part it is… (SKU or name)"
      onPick={(item) => {
        close();
        linkTo(product, item);
      }}
      onCancel={close}
    />
  );
}

/** Products the vendor added on their side: new ones first, to link or leave alone. */
export function VendorProducts({ vendor }: { vendor: Vendor }) {
  const products = useRows("vendor_product")
    .filter((p) => p.vendor_id === vendor.id)
    .sort((a, b) => Number(a.status === "ignored") - Number(b.status === "ignored") || a.family.localeCompare(b.family) || a.name.localeCompare(b.name));
  // New part from it (a dialog by the button), and the table's View and Columns menus, by the section's words
  const [making, setMaking] = useState<VendorProduct | null>(null);
  const [slot, setSlot] = useState<HTMLDivElement | null>(null);
  if (!products.length) return null;
  const fresh = products.filter((p) => p.status === "new").length;
  const editable = can("vendor_product.link");
  // New ones first, then by family and name, as they were listed
  const rows = products.map((p, order) => ({ ...p, order }));
  const columns: Column<(typeof rows)[number]>[] = [
    {
      key: "order",
      header: "",
      width: "40px",
      value: (p) => p.order,
      cell: (p) => <Thumb src={p.photos[0] ? `/files/${p.photos[0]}` : null} size={24} />,
    },
    {
      key: "name",
      header: "Name",
      width: "minmax(140px,2fr)",
      value: (p) => linkedName(p),
      cell: (p) => <span className="truncate font-medium">{linkedName(p)}</span>,
    },
    { key: "family", header: "Family", width: "minmax(90px,1fr)", cell: (p) => <span className="truncate text-muted">{p.family}</span> },
    {
      key: "vendor_sku",
      header: "Their SKU",
      width: "minmax(100px,1fr)",
      cell: (p) => <span className="truncate font-mono text-[12px] text-muted">{p.vendor_sku}</span>,
    },
    {
      key: "unit_price",
      header: "Price",
      width: "90px",
      align: "right",
      value: (p) => (p.unit_price == null ? "" : Number(p.unit_price)),
      text: (p) => (p.unit_price == null ? "" : money(p.unit_price, p.currency)),
      cell: (p) => <span className="font-mono text-[12px]">{p.unit_price != null && money(p.unit_price, p.currency)}</span>,
    },
    {
      key: "moq",
      header: "MOQ",
      width: "80px",
      align: "right",
      value: (p) => (Number(p.moq) > 0 ? Number(p.moq) : ""),
      cell: (p) => <span className="font-mono text-[12px]">{Number(p.moq) > 0 && qty(p.moq)}</span>,
    },
    {
      key: "lead_time_days",
      header: "Lead time",
      width: "90px",
      align: "right",
      value: (p) => p.lead_time_days ?? "",
      text: (p) => (p.lead_time_days == null ? "" : `${p.lead_time_days} days`),
      cell: (p) => <span className="text-[12px]">{p.lead_time_days != null && `${p.lead_time_days} days`}</span>,
    },
    {
      key: "stock",
      header: "In stock",
      width: "110px",
      align: "right",
      value: (p) => (p.stock == null ? "" : Number(p.stock)),
      text: (p) => (p.stock == null ? "" : `${qty(p.stock)}${p.stock_at ? ` (${dayText(p.stock_at)})` : ""}`),
      cell: (p) =>
        p.stock != null && (
          <span className="font-mono text-[12px]" title={p.stock_at ? `${qty(p.stock)} in stock (${dayText(p.stock_at)})` : undefined}>
            {qty(p.stock)}
          </span>
        ),
    },
    {
      key: "info",
      header: "Info",
      width: "minmax(120px,1fr)",
      optional: true,
      cell: (p) => (
        <span className="truncate text-[12px] text-muted" title={p.info}>
          {p.info}
        </span>
      ),
    },
    {
      key: "photos",
      header: "Photos",
      width: "120px",
      optional: true,
      value: (p) => p.photos.length,
      cell: (p) => (
        <CellControls className="gap-1">
          {p.photos.slice(1).map((f) => (
            <a key={f} href={`/files/${f}`} target="_blank" rel="noreferrer" className="shrink-0">
              <Thumb src={`/files/${f}`} size={24} />
            </a>
          ))}
        </CellControls>
      ),
    },
    {
      key: "status",
      header: "Status",
      width: "100px",
      value: (p) => (p.status === "ignored" ? "Left alone" : ""),
      cell: (p) => p.status === "ignored" && <Badge>Left alone</Badge>,
    },
    ...(editable
      ? [
          {
            key: "actions",
            header: "",
            width: "330px",
            value: () => null,
            cell: (p: VendorProduct) => (
              <CellControls className="gap-1">
                <Popover
                  width={460}
                  button={(toggle) => (
                    <Button size="sm" icon={<Link2 className="size-3.5" />} onClick={toggle}>
                      Link to a part
                    </Button>
                  )}
                >
                  {(close) => <LinkPicker product={p} close={close} />}
                </Popover>
                <Button size="sm" icon={<Plus className="size-3.5" />} onClick={() => setMaking(p)}>
                  New part from it
                </Button>
                <Button
                  size="sm"
                  onClick={() => mutate("vendor_product.set_status", { id: p.id, patch: { status: p.status === "ignored" ? "new" : "ignored" } })}
                >
                  {p.status === "ignored" ? "Decide later" : "Leave alone"}
                </Button>
              </CellControls>
            ),
          },
        ]
      : []),
  ];
  return (
    <Section title="Their products" count={products.length}>
      <div className="flex items-start gap-2 px-2 pb-2">
        <p className="min-w-0 flex-1 text-muted">
          {fresh > 0 && <Badge tone="warn">{fresh} to decide</Badge>} Added by {vendor.short_name || vendor.name} in the portal. Link one to a part (it becomes
          their link to it: their SKU, name and stock), or leave it alone. Until it's linked it can't go on a PO.
        </p>
        <div ref={setSlot} className="flex shrink-0 items-center gap-1" />
      </div>
      <div className="px-2 pb-2">
        <DataTable
          fit
          rows={rows}
          columns={columns}
          defaultSort={{ key: "order" }}
          exportName="Their products"
          viewsSlot={slot}
          pageFilters={false}
          rowNoun={["product", "products"]}
        />
      </div>
      {making && (
        <CreateDialog
          title="New part from their product"
          initial={{ type: "material", category: "other", name: linkedName(making), sku: "" }}
          fields={[
            { key: "type", label: "Type", type: "select", options: typeOptions() },
            { key: "category", label: "Category", type: "select", optionsFor: (v) => categoryOptions(v.type as ItemType) },
            { key: "name", label: "Name" },
            { key: "sku", label: "SKU", placeholder: "e.g. GCU2-CAP-PU", prefix: (v) => skuPrefix(typePatch({ kind: "material" }, v.type as ItemType)) },
          ]}
          onClose={() => setMaking(null)}
          onSubmit={async (v) => {
            const id = crypto.randomUUID();
            const typed = typePatch({ kind: "material" }, v.type as ItemType);
            const category = categoryOptions(v.type as ItemType).some((o) => o.value === v.category) ? categoryPatch(typed, v.category) : {};
            const ok = await mutate("item.create", {
              id,
              name: v.name,
              sku: v.sku,
              procurement: typed.kind === "subassembly" ? "make" : "buy",
              ...typed,
              ...category,
            });
            if (ok) await linkTo(making, { id, sku: v.sku });
            return ok;
          }}
        />
      )}
    </Section>
  );
}

/** On a part's vendor card: what the vendor says in the portal (theirs to change, so it isn't edited here). */
export function VendorSide({ link }: { link: VendorItem }) {
  if (link.vendor_stock == null && !link.vendor_info && !link.vendor_photos.length) return null;
  return (
    <div className="mx-3 my-1 rounded-md bg-surface-2 px-2 py-1.5 text-[12px]">
      <div className="text-muted">
        From them in the portal{link.vendor_edited_at ? `, ${ago(link.vendor_edited_at)}` : ""}
        {link.vendor_stock != null && (
          <>
            {" · "}
            <span className="text-text">{qty(link.vendor_stock)} in stock for us</span>
            {link.vendor_stock_at && ` (${dayText(link.vendor_stock_at)})`}
          </>
        )}
      </div>
      {link.vendor_info && <div className="mt-1 whitespace-pre-wrap">{link.vendor_info}</div>}
      {link.vendor_photos.length > 0 && (
        <div className="mt-1 flex flex-wrap gap-1">
          {link.vendor_photos.map((f) => (
            <a key={f} href={`/files/${f}`} target="_blank" rel="noreferrer">
              <Thumb src={`/files/${f}`} size={40} />
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Open their portal: the vendor's portal in a new tab, as them, for this staff member (a working day). What's saved there
 * is recorded as you, in the vendor portal.
 */
export function OpenPortalButton({ vendor }: { vendor: Vendor }) {
  const [busy, setBusy] = useState(false);
  if (vendor.archived || !me() || me()?.role === "receiving") return null;
  const open = async () => {
    // The tab first, while the click still counts (a tab opened after waiting is blocked as a pop-up).
    const tab = window.open("about:blank", "_blank");
    setBusy(true);
    try {
      await postJson(`/api/vendors/${vendor.id}/portal`);
      if (tab) tab.location.href = "/v";
      else location.href = "/v";
    } catch (e) {
      tab?.close();
      toast.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Button
      size="sm"
      variant="secondary"
      icon={<ExternalLink className="size-3.5" />}
      disabled={busy}
      onClick={open}
      title={`See and do what ${vendor.short_name || vendor.name}'s people do in their portal. What you save there is recorded as you.`}
    >
      Open their portal
    </Button>
  );
}
